import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Generic per-tab count provider/subscriber: each tab publishes its own count (e.g. a badge
 * number) without the parent tab strip knowing about any tab's internals.
 */
export function createTabCountContext<Tab extends string>(contextName: string) {
  type Counts = Partial<Record<Tab, number>>;

  interface ContextValue {
    counts: Counts;
    setTabCount: (tab: Tab, count: number | undefined) => void;
  }

  const Context = createContext<ContextValue | undefined>(undefined);

  function Provider({ children }: { children: React.ReactNode }) {
    const [counts, setCounts] = useState<Counts>({});

    const setTabCount = useCallback((tab: Tab, count: number | undefined) => {
      setCounts((currentCounts) => {
        const nextCounts = { ...currentCounts };

        if (typeof count === 'number') {
          nextCounts[tab] = count;
        } else {
          delete nextCounts[tab];
        }

        return nextCounts;
      });
    }, []);

    const value = useMemo(() => ({ counts, setTabCount }), [counts, setTabCount]);

    return <Context.Provider value={value}>{children}</Context.Provider>;
  }

  function useTabCounts() {
    const context = useContext(Context);

    if (!context) {
      throw new Error(`useTabCounts must be used within a ${contextName} Provider.`);
    }

    return context.counts;
  }

  function useTabCount(tab: Tab, count: number | undefined) {
    const context = useContext(Context);

    if (!context) {
      throw new Error(`useTabCount must be used within a ${contextName} Provider.`);
    }

    const { setTabCount } = context;

    useEffect(() => {
      setTabCount(tab, count);
    }, [count, setTabCount, tab]);
  }

  return { Provider, useTabCounts, useTabCount };
}
